import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Fab,
  IconButton,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardActions,
  Button
} from '@mui/material';
import {
  PhotoCamera,
  Videocam,
  FlipCameraAndroid,
  Close,
  Check,
  Stop,
  Replay,
  Download
} from '@mui/icons-material';
import { useAppBar } from '../providers/AppBarProvider';
import { useSnackbar } from '../providers/SnackBarProvider';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

const CameraCapture = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const { updateTitle, toggleCloseIcon } = useAppBar();
  
  // State para el video stream
  const [stream, setStream] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [captureMode, setCaptureMode] = useState('photo'); // 'photo' o 'video'
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [currentCamera, setCurrentCamera] = useState('user'); // 'user' o 'environment'
  const [availableCameras, setAvailableCameras] = useState([]);
  
  // Referencias
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);
  
  // Parámetros de la página anterior
  const { returnPath, allowVideo = true, allowPhoto = true, initialMode } = location.state || {};

  useEffect(() => {
    updateTitle('', t('camera.capturePhotoVideo'), t('camera.selectCaptureType'));
    toggleCloseIcon(true);
    if (initialMode) {
      setCaptureMode(initialMode);
    }
    
    initializeCamera();
    
    return () => {
      stopCamera();
      toggleCloseIcon(false);
    };
  }, []);

  useEffect(() => {
    if (stream) {
      initializeCamera();
    }
  }, [currentCamera]);

  const initializeCamera = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Obtener lista de cámaras disponibles
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(videoDevices);
      
      // Configuración de la cámara
      const constraints = {
        video: {
          facingMode: currentCamera,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: captureMode === 'video'
      };
      
      // Detener stream anterior si existe
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Obtener nuevo stream
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = newStream;
      setStream(newStream);
      
      // Asignar stream al elemento video
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
      
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError(t('camera.cameraError'));
    } finally {
      setIsLoading(false);
    }
  };

  const stopCamera = () => {
    const activeStream = streamRef.current || (videoRef.current && videoRef.current.srcObject) || stream;
    if (activeStream) {
      activeStream.getTracks().forEach(track => track.stop());
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    streamRef.current = null;
    setStream(null);
  };

  const switchCamera = () => {
    setCurrentCamera(prev => prev === 'user' ? 'environment' : 'user');
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Configurar el canvas con las dimensiones del video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Dibujar el frame actual del video en el canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const unique_id = uuidv4();
    const file_name = `${unique_id}.jpg`;

    // Convertir a blob
    canvas.toBlob((blob) => {
      const file = new File([blob], file_name, { type: 'image/jpeg' });
      setCapturedPhoto({
        file,
        url: URL.createObjectURL(blob)
      });
    }, 'image/jpeg', 0.9);
    
    //snackbar.showSnackbar('Foto capturada correctamente', 'success');
  };

  const startVideoRecording = () => {
    if (!stream) return;
    
    recordedChunksRef.current = [];
    
    try {
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9'
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const file = new File([blob], `video_${Date.now()}.webm`, { type: 'video/webm' });
        setRecordedVideo({
          file,
          url: URL.createObjectURL(blob)
        });
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      //snackbar.showSnackbar('Grabación iniciada', 'info');
      
    } catch (err) {
      console.error('Error starting recording:', err);
      //snackbar.showSnackbar('Error al iniciar la grabación', 'error');
    }
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      //snackbar.showSnackbar('Grabación finalizada', 'success');
    }
  };

  const handleCapture = () => {
    if (captureMode === 'photo') {
      capturePhoto();
    } else {
      if (isRecording) {
        stopVideoRecording();
      } else {
        startVideoRecording();
      }
    }
  };

  const resetCapture = () => {
    setCapturedPhoto(null);
    setRecordedVideo(null);
    if (isRecording) {
      stopVideoRecording();
    }
  };

  const confirmCapture = () => {
    const capturedMedia = capturedPhoto || recordedVideo;
    
    if (returnPath) {
      stopCamera();
      navigate(returnPath, { 
        state: { 
          capturedMedia: capturedMedia,
          mediaType: captureMode 
        },
        replace: true
      });
    } else {
      stopCamera();
      navigate(-1);
    }
  };

  const downloadMedia = () => {
    const media = capturedPhoto || recordedVideo;
    if (!media) return;
    
    const link = document.createElement('a');
    link.href = media.url;
    link.download = media.file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)}>
          {t('camera.back')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ p: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Selector de modo de captura */}
      {allowPhoto && allowVideo && (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={captureMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode) {
                setCaptureMode(newMode);
                resetCapture();
              }
            }}
            aria-label="capture mode"
          >
            <ToggleButton value="photo" aria-label="photo">
              <PhotoCamera sx={{ mr: 1 }} />
              {t('common.photo')}
            </ToggleButton>
            <ToggleButton value="video" aria-label="video">
              <Videocam sx={{ mr: 1 }} />
              {t('common.video')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Área de captura */}
      <Box sx={{ flex: 1, position: 'relative', backgroundColor: 'black' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}

        {/* Video preview */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: capturedPhoto || recordedVideo ? 'none' : 'block'
          }}
        />

        {/* Canvas oculto para captura de fotos */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Preview de foto capturada */}
        {capturedPhoto && (
          <img
            src={capturedPhoto.url}
            alt="Captured"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        )}

        {/* Preview de video grabado */}
        {recordedVideo && (
          <video
            src={recordedVideo.url}
            controls
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        )}

        {/* Indicador de grabación */}
        {isRecording && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              backgroundColor: 'red',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              animation: 'pulse 1s infinite'
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                backgroundColor: 'white',
                borderRadius: '50%',
                mr: 1
              }}
            />
            {t('common.recording')}
          </Box>
        )}

        {/* Controles de cámara */}
        {!capturedPhoto && !recordedVideo && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {/* Cambiar cámara */}
            {availableCameras.length > 1 && (
              <IconButton
                onClick={switchCamera}
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)'
                  }
                }}
              >
                <FlipCameraAndroid />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      {/* Controles inferiores */}
      <Box
        sx={{
          p: 2,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2
        }}
      >
        {!capturedPhoto && !recordedVideo ? (
          // Controles de captura
          <>
            <IconButton
              onClick={() => { stopCamera(); navigate(-1); }}
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
            
            <Fab
              color="primary"
              size="large"
              onClick={handleCapture}
              disabled={isLoading}
            >
              {captureMode === 'photo' ? (
                <PhotoCamera />
              ) : isRecording ? (
                <Stop />
              ) : (
                <Videocam />
              )}
            </Fab>
            
            <Box sx={{ width: 48 }} /> {/* Espaciador */}
          </>
        ) : (
          // Controles de confirmación
          <>
            <IconButton
              onClick={resetCapture}
              sx={{ color: 'white' }}
            >
              <Replay />
            </IconButton>
            
            <IconButton
              onClick={downloadMedia}
              sx={{ color: 'white' }}
            >
              <Download />
            </IconButton>
            
            <Fab
              color="primary"
              onClick={confirmCapture}
            >
              <Check />
            </Fab>
          </>
        )}
      </Box>

      {/* Información adicional */}
      {/* {capturedPhoto || recordedVideo ? (
        <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
          <Card>
            <CardContent>
              <Typography variant="h6">
                {captureMode === 'photo' ? 'Foto Capturada' : 'Video Grabado'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Archivo: {(capturedPhoto || recordedVideo)?.file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tamaño: {((capturedPhoto || recordedVideo)?.file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </CardContent>
          </Card>
        </Box>
      ) : null} */}

      {/* Estilos para animación */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </Container>
  );
};

export default CameraCapture;
